import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  analisarDocumento,
  emitirDocumento,
  enviarDocumento,
  estadoDoDocumento,
  lerEmitidos,
  lerEntreguesAnteriores,
  lerMeusContratos,
  lerMeusDocumentos,
  lerMeusEnvios,
  lerTipos,
  lerTravas,
  MAX_ARQUIVOS_POR_ENVIO,
  NOME_DA_EMISSAO,
  pedidoDePagamento,
  pedidoEmAndamento,
  ROTULO_DA_COLACAO,
  solicitarDocumento,
  urlDoAnexo,
  type ComArquivo,
  type ContratoArquivo,
  type DocumentoEmitido,
  type EntregueAnterior,
  type MeuDocumento,
  type MeuEnvio,
  type TravaDoPedido,
  type TipoDeDocumento,
} from "../lib/faculdade";
import { AvisoNavy, Selo, TituloDaSecao, data, dataDoDia } from "./FaculdadeUi";
import Carregando from "./Carregando";
import ModalPagamento from "./ModalPagamento";
import { SetaDireita } from "./ui";

// A Secretaria: o balcão de documentos da Faculdade.
//
// ---------------------------------------------------------------------------
// Os dois fluxos são o mesmo balcão, e a tela os separa
// ---------------------------------------------------------------------------
// **Envio** é o que o aluno manda (RG, diploma, comprovante) e a secretaria
// aprova ou reprova. **Solicitação** é o que ele pede (declaração, histórico) e
// a secretaria entrega. Na tabela é uma coisa só; na tela são duas, porque o que
// o aluno FAZ em cada uma é oposto.
//
// ---------------------------------------------------------------------------
// O documento enviado é do ALUNO, não do curso (000286)
// ---------------------------------------------------------------------------
// Decisão de Victor em 22/09/2026: *"Fui na secretaria de DP e enviei os 4
// documentos obrigatórios e foram aprovados. Esses mesmos devem valer para todas
// as outras Pós que eu tiver."* Até ali era como o Bubble funcionava — o
// documento era do par (aluno, curso), e a tela mostrava "Ainda não enviado para
// este curso" a quem já tinha o RG aprovado na pós do lado. Medido naquele dia:
// 716 alunos têm o mesmo documento em mais de um curso, e 707 pares (aluno,
// curso) passaram a ter os documentos resolvidos com a mudança.
//
// O card mostra o que VALE (o aprovado mais novo, de qualquer curso) e, quando o
// aluno mandou de novo depois dele, a tentativa — sem que ela desfaça nada. Quem
// decide qual é qual é o banco (`sec_meus_envios`, sobre a mesma regra das
// travas); aqui só se escolhe o que desenhar. O curso de cada envio continua
// aparecendo, como PROCEDÊNCIA: "enviado no curso X" é o que explica ao aluno por
// que um documento que ele não mandou nesta pós já está aprovado nela.
//
// ---------------------------------------------------------------------------
// A mensagem da reprovação é o conteúdo, não o rodapé
// ---------------------------------------------------------------------------
// 67.664 documentos têm mensagem da equipe. É ali que ela diz o que corrigir, e
// um "Reprovado" sem o motivo à vista devolve o aluno ao balcão sem saber o que
// fazer — ele reenvia igual. Por isso a mensagem aparece em destaque na linha
// reprovada, e não atrás de um clique.

/**
 * O que uma ação devolve à tela.
 *
 * `mudou` existe por causa da análise automática. Uma reprovação da IA é
 * `tom: "erro"` — o aluno TEM o que corrigir — e ao mesmo tempo mudou o
 * documento, então a lista precisa ser relida: é ali que o bloco "O que precisa
 * mudar" aparece, com o motivo em destaque. Sem este campo o pai relia só em
 * `tom: "ok"`, e o aluno leria "a conferência apontou o que precisa mudar" sem
 * nada apontado na tela — a mesma família do "Limite com a prorrogação" que
 * desaparecia no ramo em que era mais necessário.
 */
type Recado = { tom: "ok" | "erro"; texto: string; mudou?: boolean };

export default function FaculdadeSecretaria({
  cursoAtivo,
  cursos,
}: {
  cursoAtivo: string;
  cursos: { slug: string; nome: string }[];
}) {
  const [fase, setFase] = useState<"carregando" | "pronto" | "erro">("carregando");
  const [tipos, setTipos] = useState<TipoDeDocumento[]>([]);
  const [docs, setDocs] = useState<MeuDocumento[]>([]);
  const [envios, setEnvios] = useState<MeuEnvio[]>([]);
  const [emitidos, setEmitidos] = useState<DocumentoEmitido[]>([]);
  const [anteriores, setAnteriores] = useState<EntregueAnterior[]>([]);
  const [contratos, setContratos] = useState<ContratoArquivo[]>([]);
  const [travas, setTravas] = useState<TravaDoPedido[]>([]);
  const [recado, setRecado] = useState<{ tom: "ok" | "erro"; texto: string } | null>(null);

  // O curso é parâmetro: as travas 4 e 5 contam as avaliações da TURMA e o tempo
  // desde a MATRÍCULA, então cada curso do aluno tem um estado próprio. Carregar
  // uma vez e filtrar em memória não daria — não há o que filtrar.
  async function recarregar(curso: string) {
    const [t, d, n, e, v, a, c] = await Promise.all([
      lerTipos(),
      lerMeusDocumentos(),
      lerMeusEnvios(),
      lerEmitidos(),
      lerTravas(curso),
      lerEntreguesAnteriores(),
      lerMeusContratos(),
    ]);
    setTipos(t);
    setDocs(d);
    setEnvios(n);
    setEmitidos(e);
    setTravas(v);
    setAnteriores(a);
    setContratos(c);
  }

  // Depende de `cursoAtivo`: trocar de curso troca as travas. Sem a dependência,
  // o aluno com duas pós veria as travas da primeira pós na tela da segunda — e
  // elas divergem de verdade (turma diferente, matrícula em data diferente).
  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        await recarregar(cursoAtivo);
        if (vivo) setFase("pronto");
      } catch {
        if (vivo) setFase("erro");
      }
    })();
    return () => {
      vivo = false;
    };
  }, [cursoAtivo]);

  /**
   * Relê depois de uma ação que deu certo. NUNCA lança.
   *
   * Os dois `aoTerminar` faziam `await recarregar(...)` nu, e as quatro leituras
   * de dentro dela fazem `throw error`. Uma falha de rede ali produzia rejeição
   * não tratada E uma tela contraditória: o aviso do topo dizia "Pronto —
   * Declaração emitida", a lista continuava sem o pedido, e o botão "Pedir"
   * seguia ligado — porque `docs` não havia sido atualizado. O clique seguinte
   * criava um SEGUNDO pedido e queimava outro número, já que a declaração
   * acabara de virar `entregue` e a checagem de duplicado não alcança esse estado.
   *
   * O desenho segue o princípio da casa: erro de LEITURA e erro de ESCRITA não
   * têm o mesmo tamanho. A escrita deu certo — o documento existe. O que falhou
   * foi atualizar a tela, e é a TELA que pede recarga, não o pedido que se
   * perdeu. Por isso a mensagem diz "nada se perdeu".
   */
  async function relerDepoisDaAcao(curso: string) {
    try {
      await recarregar(curso);
    } catch {
      setRecado({
        tom: "erro",
        texto:
          "O seu pedido foi registrado, mas não conseguimos atualizar a lista. " +
          "Recarregue a página para ver o resultado — nada se perdeu.",
      });
    }
  }

  if (fase === "carregando") return <Carregando />;
  if (fase === "erro") {
    return (
      <AvisoNavy titulo="Não conseguimos carregar a secretaria" tom="erro">
        Recarregue a página. Se continuar, use o canal de suporte do seu curso.
      </AvisoNavy>
    );
  }

  const tiposDeEnvio = tipos.filter((t) => t.fluxo === "envio");
  const tiposDePedido = tipos.filter((t) => t.fluxo === "solicitacao");
  // Os ENVIOS vêm de `sec_meus_envios` (000286) e os pedidos de
  // `sec_meus_documentos`: uma porta por fluxo, porque o envio é do aluno e o
  // pedido é do curso.
  const meusPedidos = docs.filter((d) => d.fluxo === "solicitacao");

  const nomeDoCurso = (slug: string | null) =>
    slug ? (cursos.find((c) => c.slug === slug)?.nome ?? slug) : "Sem curso";

  return (
    <>
      {recado && (
        <div className="mb-6">
          <AvisoNavy
            titulo={recado.tom === "ok" ? "Pronto" : "Não deu certo"}
            tom={recado.tom === "ok" ? "espera" : "erro"}
          >
            {recado.texto}
          </AvisoNavy>
        </div>
      )}

      {/* ── Os documentos que o aluno envia ────────────────────────────── */}
      <TituloDaSecao nota="A secretaria confere e responde por aqui">
        Meus documentos
      </TituloDaSecao>

      {/* Dito uma vez, no alto, e só a quem tem mais de uma pós: é para ele que a
          regra muda alguma coisa, e repeti-la em cada card seria a mesma frase
          quatro vezes (a cicatriz do "sem dono" 27 vezes na Visão do Catálogo). */}
      {cursos.length > 1 && (
        <p data-documento-por-aluno className="-mt-2 mb-4 text-sm text-navy-mute">
          Estes documentos valem para todos os seus cursos: o que foi aprovado numa pós não precisa
          ser enviado de novo nas outras.
        </p>
      )}

      <div className="flex flex-col gap-3">
        {tiposDeEnvio.map((tipo, i) => (
          <BlocoDeEnvio
            key={tipo.id}
            tipo={tipo}
            cursoAtivo={cursoAtivo}
            envios={envios.filter((e) => e.tipo_id === tipo.id)}
            nomeDoCurso={nomeDoCurso}
            i={i}
            aoTerminar={async (r) => {
              setRecado(r);
              if (r.tom === "ok" || r.mudou) await relerDepoisDaAcao(cursoAtivo);
            }}
          />
        ))}

        {/* Os enviados que NÃO casam com o catálogo de hoje. Eram 11 nomes
            históricos ("RG (Frente)", "CPF (Verso)"…) que o catálogo de fev/2026
            unificou em "Documento de Identificação", perdendo a face. Desde a
            consolidação de 04/09 são ZERO, e o bloco fica para o dia em que um
            tipo for desligado: escondê-los faria o aluno achar que o documento
            sumiu. */}
        {(() => {
          const fora = envios.filter((e) => !tiposDeEnvio.some((t) => t.id === e.tipo_id));
          if (!fora.length) return null;
          return (
            <div className="tv-card p-5">
              <h3 className="font-display font-semibold text-white">Enviados antes</h3>
              <p className="mt-1 text-sm text-navy-mute">
                Documentos que você enviou quando a lista tinha outros nomes. Eles continuam
                valendo.
              </p>
              <div className="mt-4 flex flex-col gap-2">
                {fora.map((d) => (
                  <LinhaDeDocumento key={d.id} doc={d} nomeDoCurso={nomeDoCurso} />
                ))}
              </div>
            </div>
          );
        })()}
      </div>

      {/* ── Os documentos que o aluno pede ─────────────────────────────── */}
      <div className="mt-9">
        <TituloDaSecao nota="O prazo é o que a secretaria promete para cada um">
          Pedir um documento
        </TituloDaSecao>

        <div className="grid gap-3 sm:grid-cols-2">
          {tiposDePedido.map((tipo, i) => (
            <BlocoDePedido
              key={tipo.id}
              tipo={tipo}
              trava={travas.find((v) => v.tipo_id === tipo.id) ?? null}
              cursoAtivo={cursoAtivo}
              meus={meusPedidos.filter((d) => d.tipo_id === tipo.id)}
              i={i}
              aoTerminar={async (r) => {
                setRecado(r);
                if (r.tom === "ok" || r.mudou) await relerDepoisDaAcao(cursoAtivo);
              }}
            />
          ))}
        </div>

        {(() => {
          const fora = meusPedidos.filter((d) => !tiposDePedido.some((t) => t.id === d.tipo_id));
          if (!fora.length) return null;
          return (
            <div className="tv-card mt-3 p-5">
              <h3 className="font-display font-semibold text-white">Pedidos anteriores</h3>
              <p className="mt-1 text-sm text-navy-mute">
                Pedidos feitos quando a lista tinha outros nomes.
              </p>
              <div className="mt-4 flex flex-col gap-2">
                {fora.map((d) => (
                  <LinhaDeDocumento key={d.id} doc={d} nomeDoCurso={nomeDoCurso} />
                ))}
              </div>
            </div>
          );
        })()}

        {/* ── O que já foi entregue e um pedido mais novo escondeu (000260) ──
            `sec_meus_documentos` devolve UM por (tipo, curso) — o mais novo.
            Sem esta lista, pedir o mesmo documento de novo faz o anterior, JÁ
            ENTREGUE e com PDF, desaparecer da tela. Era assim para todo mundo,
            e ficou visível quando os 148 certificados digitais entregues foram
            para o tipo ativo: 142 deles têm um pedido mais novo em aberto.

            O download em si nunca dependeu desta lista — a policy do bucket
            libera pela PASTA do caminho. Quem depende dela é o aluno, que
            precisa do botão para chegar ao arquivo. */}
        {anteriores.length > 0 && (
          <div className="tv-card mt-3 p-5" data-entregues-antes={anteriores.length}>
            <h3 className="font-display font-semibold text-white">Entregues antes</h3>
            <p className="mt-1 text-sm text-navy-mute">
              Documentos que a secretaria já entregou a você e que um pedido mais novo do mesmo
              documento deixou de mostrar acima. Eles continuam valendo.
            </p>
            <div className="mt-4 flex flex-col gap-2">
              {anteriores.map((a) => (
                <LinhaDeArquivo
                  key={a.id}
                  nome={a.nome}
                  detalhe={`${nomeDoCurso(a.curso_slug)} · entregue em ${data(a.decidido_em ?? a.criado_em)}`}
                  arquivo={a}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── O contrato assinado (000261) ───────────────────────────────────
          Ele saiu de `sec_documentos` porque contrato não é documento pedido ao
          balcão — estava lá porque era assim que o Bubble o mostrava. Sai da
          lista de pedidos e ganha seção própria, lendo `contratos_arquivos` por
          função de dono. Nenhum PDF se perdeu no caminho. */}
      {contratos.length > 0 && (
        <div className="mt-9" data-contratos={contratos.length}>
          <TituloDaSecao nota={`${contratos.length} documento(s)`}>
            Contratos assinados
          </TituloDaSecao>
          <div className="tv-card flex flex-col gap-2 p-5">
            {contratos.map((c) => (
              <LinhaDeArquivo
                key={c.id}
                nome={c.nome}
                detalhe={`${nomeDoCurso(c.curso_slug)} · ${
                  c.assinado_em ? `assinado em ${data(c.assinado_em)}` : data(c.criado_em)
                }`}
                arquivo={c}
              />
            ))}
          </div>
        </div>
      )}

      {/* ── Os documentos oficiais já emitidos ─────────────────────────── */}
      {emitidos.length > 0 && (
        <div className="mt-9" data-emitidos={emitidos.length}>
          <TituloDaSecao nota={`${emitidos.length} documento(s)`}>
            Documentos emitidos para você
          </TituloDaSecao>
          {/* Os emitidos pelo sistema anterior não trazem arquivo (000287), e a
              ausência do botão numa linha precisa de explicação — senão ela se
              lê como defeito, ou como documento perdido. Dito UMA vez, e só se
              houver linha sem arquivo: quem só tem emitidos do hub não lê nada
              aqui. */}
          {emitidos.some((e) => !e.anexo_path) && (
            <p data-emitidos-sem-arquivo className="-mt-2 mb-4 text-sm text-navy-mute">
              Os emitidos pelo sistema anterior guardam o número e o código de verificação, mas não o
              arquivo. Quando a secretaria o entregou, ele está no pedido — em{" "}
              <Link
                to="/meus-documentos"
                className="font-semibold text-laranja-claro underline decoration-laranja/40 underline-offset-2 hover:decoration-laranja"
              >
                Meus documentos
              </Link>
              .
            </p>
          )}
          <div className="flex flex-col gap-2">
            {emitidos.map((e) => (
              <div
                key={e.id}
                data-emitido={e.id}
                className="tv-card flex flex-wrap items-center gap-x-6 gap-y-2 p-4"
              >
                <div className="min-w-56 flex-1">
                  <p className="font-semibold text-white">
                    {/* Pelo NOME do tipo, nunca por ternário: antes da 000237
                        tudo que não fosse certificado saía como "Declaração",
                        e o histórico parcial teria sido rotulado errado sem
                        nenhum erro na tela. */}
                    {NOME_DA_EMISSAO[e.tipo] ?? "Documento"}
                    <span className="ml-2 font-normal text-navy-mute">nº {e.numero}</span>
                  </p>
                  <p className="mt-0.5 text-sm text-navy-mute">
                    {e.curso_origem ?? nomeDoCurso(e.curso_slug)} · emitido em {data(e.emitido_em)}
                  </p>
                </div>
                {/* O código de verificação é o que VALIDA o documento — 6.338 são
                    distintos, e os NÚMEROS repetem (113 deles). Mostrar o código
                    é o que permite a outra ponta conferir. */}
                <p className="font-mono text-sm tracking-wider text-navy-ink">
                  {e.codigo_verificacao}
                </p>
                {/* O arquivo da EMISSÃO (000287), quando ela registrou um — as do
                    hub. O banco só o devolve se a emissão tem pedido entregue, que
                    é o que a policy do bucket exige: o botão nunca aponta para um
                    download que a url assinada recusaria. */}
                {e.anexo_path && (
                  <BotaoDeAnexo doc={{ anexo_path: e.anexo_path, anexo_externo_url: null }} rotulo="Baixar" />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function BlocoDeEnvio({
  tipo,
  cursoAtivo,
  envios,
  nomeDoCurso,
  i,
  aoTerminar,
}: {
  tipo: TipoDeDocumento;
  cursoAtivo: string;
  /** As linhas deste tipo em `sec_meus_envios`: o que vale e, quando é outra, a tentativa mais nova. */
  envios: MeuEnvio[];
  nomeDoCurso: (s: string | null) => string;
  i: number;
  aoTerminar: (r: Recado) => void;
}) {
  const [enviando, setEnviando] = useState(false);
  const campo = useRef<HTMLInputElement>(null);

  // O documento é do ALUNO (000286). O card desenha o que VALE — o aprovado mais
  // novo, de qualquer curso —, e na falta dele a tentativa mais nova. Qual é
  // qual vem MARCADO do banco, pela mesma regra que as travas leem; aqui não se
  // compara status nem data, senão a tela e a trava teriam duas definições de
  // "o documento que conta".
  const vale = envios.find((e) => e.vale) ?? null;
  const atual = envios.find((e) => e.atual) ?? null;
  const principal = vale ?? atual;
  // A tentativa mais nova quando ela NÃO é a que vale: o aluno mandou de novo
  // depois do aprovado. Ela não desfaz nada — e a tela diz isso, porque sem esta
  // linha quem reenviou leria só "Aprovado" e não saberia o que aconteceu com o
  // que acabou de mandar.
  const tentativa = vale && atual && atual.id !== vale.id ? atual : null;
  const estado = principal ? estadoDoDocumento({ fluxo: "envio", status: principal.status }) : null;

  /**
   * " no curso X" quando o envio foi feito em OUTRO curso — e nada quando foi
   * neste. É a peça que explica ao aluno por que um documento que ele não mandou
   * nesta pós já aparece aprovado nela. O nome vem do banco: o curso de origem
   * pode ter expirado, e aí a lista de cursos da tela não o tem.
   */
  const procedencia = (e: MeuEnvio) =>
    e.curso_slug && e.curso_slug !== cursoAtivo
      ? ` no curso ${e.curso_nome ?? nomeDoCurso(e.curso_slug)}`
      : "";

  /**
   * Recebe UM ou VÁRIOS arquivos num envio só (até 5).
   *
   * Decisão de Victor em 02/09, depois de ver a cadeia real: um aluno mandou o
   * histórico em 9 envios de 20 em 20 segundos, e como a tela mostra só o último
   * ele leu "Reprovado: não contém o nome" — as páginas do meio de um histórico
   * não repetem o nome, e as sete aprovadas ficaram invisíveis.
   *
   * O `multiple` no input é o que muda isso: as páginas viram UM documento, e a
   * secretaria (e o Gemini) avaliam o conjunto.
   */
  async function escolher(e: React.ChangeEvent<HTMLInputElement>) {
    const escolhidos = Array.from(e.target.files ?? []);
    // Limpa o campo SEMPRE, e antes de qualquer await: sem isso, escolher os
    // mesmos arquivos de novo (depois de um erro) não dispara `change` e a tela
    // parece travada.
    e.target.value = "";
    if (!escolhidos.length) return;

    // A tela diz o limite ANTES de subir nada — o banco também recusa (a
    // constraint da 000162), mas "violação de check" depois de cinco uploads é
    // trabalho jogado fora, e a mensagem não diz o que fazer.
    if (escolhidos.length > MAX_ARQUIVOS_POR_ENVIO) {
      aoTerminar({
        tom: "erro",
        texto:
          `Você escolheu ${escolhidos.length} arquivos, e o limite é ${MAX_ARQUIVOS_POR_ENVIO} por documento. ` +
          `Junte as páginas num PDF só, ou envie as ${MAX_ARQUIVOS_POR_ENVIO} principais.`,
      });
      return;
    }

    setEnviando(true);
    const r = await enviarDocumento(tipo.id, cursoAtivo, escolhidos);
    if (!r.ok) {
      setEnviando(false);
      aoTerminar({ tom: "erro", texto: r.erro });
      return;
    }

    // ── a análise, no MESMO clique ───────────────────────────────────────────
    //
    // É o que o Bubble faz hoje: o aluno sobe o arquivo e a IA responde na hora.
    // `analisarDocumento` NUNCA lança — se ela falhar, o envio já está no bucket
    // e na fila, e dizer "não conseguimos enviar" faria o aluno reenviar um
    // documento que está lá. A distinção entre erro de LEITURA e erro de ESCRITA
    // é a cicatriz do Calendário: sem UMA escrita, há tela e falta um tique.
    const a = await analisarDocumento(r.id);
    setEnviando(false);

    const quantos = escolhidos.length === 1
      ? `${tipo.nome} enviado`
      : `${tipo.nome} enviado com ${escolhidos.length} páginas`;

    if (!a.analisado) {
      // A análise não rodou. O envio ESTÁ registrado, e é isso que a frase diz —
      // a secretaria atende pela fila, que é o caminho que já existe.
      aoTerminar({ tom: "ok", texto: `${quantos}. A secretaria vai conferir.` });
      return;
    }

    aoTerminar(
      a.aplicou === "aprovado"
        ? { tom: "ok", texto: `${quantos} e aprovado na conferência automática.` }
        : a.aplicou === "reprovado"
          // O motivo NÃO é repetido aqui: ele já está gravado em
          // `sec_documentos.mensagem` e aparece no bloco "O que precisa mudar",
          // em destaque, multi-linha. Repeti-lo num aviso que desaparece seria
          // dizer duas vezes a mesma coisa e dizê-la pior — mas para ele
          // aparecer a lista tem de ser RELIDA, e é o que `mudou` pede.
          ? {
              tom: "erro",
              texto: `${quantos}, e a conferência automática apontou o que precisa mudar.`,
              mudou: true,
            }
          // `nada` — a falha foi NOSSA. O aluno não tem o que fazer, e a frase
          // não pode sugerir que tenha: 86,3% das reprovações de "não foi
          // possível ler" da origem vieram de indisponibilidade do serviço.
          : { tom: "ok", texto: `${quantos}. A secretaria vai conferir.` },
    );
  }

  return (
    <div
      data-tipo-envio={tipo.id}
      // Os ids que o card desenha, para a prova de tela conferir contra a
      // resposta do banco sem ler texto — escopo nomeado, a cicatriz das sete
      // etiquetas iguais da calculadora.
      data-envio-vale={vale?.id ?? ""}
      data-envio-atual={atual?.id ?? ""}
      className={`tv-card rise p-5 ${principal?.status === "reprovado" ? "border-laranja/40" : ""}`}
      style={{ "--i": Math.min(i + 1, 8) } as React.CSSProperties}
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-56 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-display font-semibold text-white">{tipo.nome}</h3>
            {estado && <Selo tom={estado.tom}>{estado.rotulo}</Selo>}
            {/* A data que o diploma estampa (000278), ao lado do nome do
                documento — que é onde Victor pediu que ela ficasse.

                Só em documento APROVADO, e isto foi medido em 22/09, na
                primeira coleta real: dos 6.169 diplomas reprovados, **137 foram
                reprovados por trazerem o nome de OUTRA PESSOA** e 263 por serem
                documento incompatível. A IA lê a data de qualquer papel que
                receba — e ela está certa em ler —, mas estampar "Colou grau em
                15/12/2018" no card de quem mandou o diploma do irmão é afirmar
                um fato sobre o aluno que a casa acabou de recusar.
                A data continua no banco: ela serve à secretaria e à coleta. O
                que não acontece é a tela apresentá-la como se fosse dele.

                O RÓTULO vem da procedência e não é enfeite: um diploma traz até
                três datas, e chamar de "colou grau" a de expedição é afirmar o
                que o documento não afirma. Ver `ROTULO_DA_COLACAO`.

                Cor FIXA, não semântica: isto vive dentro do `tv-card`, que é
                navy nos dois temas — `text-tinta` aqui clarearia no escuro e
                sumiria, que é a cicatriz dos cards em escada do login.

                Desde a 000286 a data é a do documento que VALE: `principal` só
                é aprovado quando é o `vale`, e a tentativa mais nova nunca
                empresta a data dele (o caso 12 do teste 87). */}
            {principal?.status === "aprovado" && principal.colacao_data && principal.colacao_origem && (
              <span
                data-colacao={principal.colacao_origem}
                className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-navy-ink/10 px-2.5 py-0.5 text-xs"
              >
                <span className="text-navy-mute">
                  {ROTULO_DA_COLACAO[principal.colacao_origem].titulo}
                </span>
                <span className="font-semibold text-white">{dataDoDia(principal.colacao_data)}</span>
              </span>
            )}
          </div>
          {principal ? (
            <>
              <p data-envio-procedencia={principal.curso_slug ?? ""} className="mt-1 text-sm text-navy-mute">
                Enviado em {data(principal.criado_em)}
                {procedencia(principal)}
                {principal.decidido_em ? ` · respondido em ${data(principal.decidido_em)}` : ""}
                {/* A contagem é do ALUNO, em todos os cursos, e só cabe quando o
                    documento desenhado é o mais novo: com uma tentativa depois
                    dele, "N envios antes deste" seria falso — e a tentativa já
                    está dita logo abaixo. */}
                {!tentativa && principal.envios > 1 ? ` · ${principal.envios - 1} envio(s) antes deste` : ""}
              </p>
              {/* A ressalva fica FORA do chip, e existir é o ponto: sem ela, uma
                  data de conclusão ao lado do nome é lida como colação de grau.
                  A tela diz o que o documento não diz.

                  Mesma condição do chip, e não só "tem origem": sem o chip ao
                  lado, "o diploma não informa a data de colação" num diploma
                  reprovado seria uma ressalva sobre uma data que a tela não
                  mostra. */}
              {principal.status === "aprovado" &&
                principal.colacao_data &&
                principal.colacao_origem &&
                ROTULO_DA_COLACAO[principal.colacao_origem].nota && (
                  <p className="mt-0.5 text-xs text-navy-mute">
                    {ROTULO_DA_COLACAO[principal.colacao_origem].nota}
                  </p>
                )}
            </>
          ) : (
            <p className="mt-1 text-sm text-navy-mute">
              Ainda não enviado.
              {/* Dito ANTES do clique, porque é o que muda o comportamento: sem
                  isso o aluno manda uma página por envio, que é como os 3.850
                  slots com repetição nasceram. */}
              <span className="mt-0.5 block text-xs">
                Tem mais de uma página? Escolha até {MAX_ARQUIVOS_POR_ENVIO} arquivos de uma vez.
              </span>
            </p>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-3">
          {principal && (principal.anexo_path || principal.anexo_externo_url) && (
            <BotaoDeAnexo doc={principal} />
          )}
          <label className={`pill-cta cursor-pointer ${enviando ? "opacity-60" : ""}`}>
            {enviando ? "Enviando…" : principal ? "Enviar de novo" : "Enviar"}
            <input
              ref={campo}
              type="file"
              className="sr-only"
              // `multiple` (000162/000163): o aluno escolhe as páginas de uma
              // vez e chega UM documento. Sem isto ele mandava uma por envio, e
              // a tela mostrava só a última.
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif,application/pdf,image/*"
              disabled={enviando}
              onChange={escolher}
            />
          </label>
        </div>
      </div>

      {/* A mensagem da reprovação, em destaque. É o conteúdo da linha, não um
          detalhe: sem ela o aluno reenvia o mesmo documento.

          `whitespace-pre-line` porque a mensagem é MULTI-LINHA: os
          `motivos_reprovacao` do prompt são um ARRAY, e a mensagem é o join
          deles por `\n`. Medido: 4.492 mensagens da base têm mais de uma linha,
          e sem isto elas viravam parágrafo corrido — três motivos separados
          lidos como uma frase só. */}
      {principal?.status === "reprovado" && principal.mensagem && (
        <p className="mt-4 whitespace-pre-line rounded-cf border border-laranja/30 bg-laranja/10 p-3 text-sm text-navy-ink">
          <span className="font-semibold text-laranja-claro">O que precisa mudar: </span>
          {principal.mensagem}
        </p>
      )}

      {/* A TENTATIVA mais nova, quando ela não é a que vale (000286).
          Medido em 22/09: 114 slots tinham o envio mais novo reprovado e um
          aprovado antes dele — 75 com "Nome do aluno não encontrado", a página
          do meio de um histórico mandada sozinha. Pela regra antiga aquilo
          derrubava o documento; agora não derruba, e a tela diz as duas coisas:
          o que aconteceu com o envio novo, e que o aprovado continua valendo.

          Não é a caixa laranja de "O que precisa mudar": ali há algo a fazer, e
          aqui não há — pintar de alerta um documento que está valendo mandaria
          o aluno reenviar sem motivo. */}
      {tentativa && (
        <p
          data-tentativa={tentativa.status}
          className="mt-4 whitespace-pre-line rounded-cf border border-navy-ink/15 bg-navy-ink/5 p-3 text-sm text-navy-mute"
        >
          {tentativa.status === "reprovado"
            ? `Você enviou este documento de novo em ${data(tentativa.criado_em)}${procedencia(tentativa)}, e esse envio foi reprovado${
                tentativa.mensagem ? `: ${tentativa.mensagem}` : "."
              }`
            : `Você enviou este documento de novo em ${data(tentativa.criado_em)}${procedencia(tentativa)}, e o novo envio está em análise.`}{" "}
          <span className="font-semibold text-white">
            {tentativa.status === "reprovado"
              ? "O documento aprovado continua valendo — você não precisa fazer nada."
              : "Enquanto isso, o aprovado continua valendo."}
          </span>
        </p>
      )}
    </div>
  );
}

function BlocoDePedido({
  tipo,
  trava,
  cursoAtivo,
  meus,
  i,
  aoTerminar,
}: {
  tipo: TipoDeDocumento;
  /**
   * O que o banco diz sobre pedir ESTE documento neste curso.
   *
   * Nulo só aconteceria se o tipo não viesse na lista de travas — hoje
   * impossível, porque as duas leituras partem dos mesmos `sec_tipos` ativos de
   * solicitação. Tratar o nulo como "não sei" e deixar o botão LIGADO é melhor
   * que travar por ausência de dado: a RPC valida de todo modo, e botão
   * desligado sem motivo à vista é justamente o que esta peça existe para
   * evitar.
   */
  trava: TravaDoPedido | null;
  cursoAtivo: string;
  meus: MeuDocumento[];
  i: number;
  aoTerminar: (r: Recado) => void;
}) {
  const [pedindo, setPedindo] = useState(false);
  const [pagando, setPagando] = useState(false);
  const doCurso = meus.find((d) => d.curso_slug === cursoAtivo);
  const estado = doCurso ? estadoDoDocumento(doCurso) : null;
  // A lista de status vive em `pedidoEmAndamento`, e não aqui: ela já foi
  // esquecida em quatro lugares quando `aguardando_conferencia` nasceu (o porquê
  // inteiro está no comentário da função). Certificado em conferência É pedido em
  // andamento — mostrar "Pedir de novo" ali oferece um clique que a RPC recusa.
  const emAndamento = pedidoEmAndamento(doCurso?.status);

  // `sem_sessao` fica de fora: se a sessão caiu, o aluno vai receber a tela de
  // login inteira, e mostrar "entre de novo" dentro de um cartão de pedido seria
  // o mesmo recado no lugar errado. `tipo_desconhecido` também — ele significa
  // que o catálogo mudou entre as duas leituras, e a única coisa útil é o botão
  // tentar (a RPC recusa com a mensagem certa).
  const travaViva =
    trava && trava.motivo !== "sem_sessao" && trava.motivo !== "tipo_desconhecido" ? trava : null;
  const podePedir = trava === null ? true : trava.pode;

  /**
   * Pede o documento e, no mesmo clique, manda emitir.
   *
   * Os dois passos juntos porque é assim que o Bubble funciona hoje, e é esse
   * comportamento que está sendo portado (item 4 do pedido de Victor em 01/09):
   * *"quando o aluno solicita a declaração ou certificado, o sistema envia o
   * pedido pro n8n que emite o doc com os dados do aluno"*.
   *
   * A emissão NUNCA derruba o pedido. Ela pode falhar por três razões — falta
   * dado cadastral, o tipo não tem modelo automático, o emissor está fora — e em
   * todas o pedido já existe e continua na fila da equipe, que é o caminho manual
   * que sempre existiu. Então o recado diz as duas coisas: o pedido entrou, e o
   * que aconteceu com a emissão.
   */
  async function pedir() {
    setPedindo(true);
    const r = await solicitarDocumento(tipo.id, cursoAtivo);
    if (!r.ok) {
      setPedindo(false);
      aoTerminar({ tom: "erro", texto: r.erro });
      return;
    }

    const emissao = await emitirDocumento(r.id);
    setPedindo(false);

    if (emissao.emitido) {
      aoTerminar({
        tom: "ok",
        texto: emissao.emConferencia
          ? // O certificado NÃO fica disponível na hora: passa pela validação
            // manual da secretaria. Dizer isso agora evita o aluno voltar amanhã
            // procurando o download e concluir que o pedido não andou.
            //
            // E o NÚMERO não entra nesta frase. A 000154 nasceu justamente porque
            // a tela mostrava "Certificado nº CERT-2026-999999" três blocos
            // abaixo de "Em conferência pela secretaria": se a secretaria recusar
            // — que é o ponto de existir a conferência —, o aluno já anotou um
            // número que nunca valeu. Consertar a lista e deixar o número voltar
            // pela mensagem seria fechar a porta e abrir a janela.
            `${tipo.nome} emitido. A secretaria confere e libera para você — você recebe um e-mail quando estiver pronto.`
          : // Na declaração o número JÁ vale: ela é entregue no mesmo ato.
            `${tipo.nome} emitido (${emissao.numero}). Já está aqui para baixar.`,
      });
      return;
    }

    // O pedido entrou. A emissão não saiu — e o `detalhe` só vai para a tela
    // quando a recusa é ACIONÁVEL pelo aluno ("Complete no seu perfil: CPF, RG").
    //
    // Falha de emissor, endereço não configurado, curso sem parâmetro: nada disso
    // é dele, e mostrar "defina N8N_BASE_URL nos secrets" a quem só quer uma
    // declaração é a falta que a rota pública de orçamento não pode ter — quem lê
    // não tem suporte para acionar. Nesses casos a tela diz a verdade que
    // importa: o pedido entrou, e a secretaria prepara à mão.
    const prazo = tipo.prazo_dias ? ` O prazo da secretaria é de ${tipo.prazo_dias} dias.` : "";
    aoTerminar({
      tom: "ok",
      texto:
        emissao.paraOAluno && emissao.detalhe
          ? `${tipo.nome} solicitado. ${emissao.detalhe}`
          : `${tipo.nome} solicitado. A secretaria vai preparar o documento.${prazo}`,
    });
  }

  return (
    <div
      data-tipo-pedido={tipo.id}
      className="tv-card rise flex flex-col p-5"
      style={{ "--i": Math.min(i + 1, 8) } as React.CSSProperties}
    >
      <div className="flex items-start justify-between gap-2">
        <h3 className="font-display font-semibold text-white">{tipo.nome}</h3>
        {estado && <Selo tom={estado.tom}>{estado.rotulo}</Selo>}
      </div>

      <p className="mt-1 text-sm text-navy-mute">
        {tipo.prazo_dias ? `Prazo de ${tipo.prazo_dias} dias` : "Sem prazo definido"}
        {tipo.exige_pagamento ? " · este documento tem custo" : ""}
      </p>

      {doCurso && (
        <p className="mt-2 text-xs text-navy-mute">
          Pedido em {data(doCurso.criado_em)}
          {doCurso.decidido_em ? ` · entregue em ${data(doCurso.decidido_em)}` : ""}
        </p>
      )}

      {/* O que falta o ALUNO fazer. Só aparece quando a bola está com ele —
          "aguardando pagamento" é o único caso, e eram 1.129 pedidos reais nesse
          estado. Sem esta linha ele espera por algo que não acontece sozinho.

          000242: quando o estado é de pagamento, a ação deixou de ser uma FRASE
          e virou um BOTÃO — a frase antiga ("fale com a secretaria para pagar")
          mandava o aluno procurar um caminho que não existia na tela, e para os
          341 pedidos de aluno FBC medidos em 09/09 ela mandava pagar por algo
          gratuito. */}
      {doCurso && pedidoDePagamento(doCurso.status) ? (
        <button
          onClick={() => setPagando(true)}
          data-acao="abrir-pagamento"
          className="pill-cta mt-3 self-start"
        >
          {doCurso.status === "aguardando_pagamento"
            ? "Pagar e anexar comprovante"
            : "Ver o meu comprovante"}
        </button>
      ) : (
        estado?.acao && (
          <p className="mt-2 text-sm font-semibold text-laranja-claro">{estado.acao}</p>
        )
      )}

      {doCurso && pagando && (
        <ModalPagamento
          documentoId={doCurso.id}
          nomeDoTipo={tipo.nome}
          aberto={pagando}
          aoFechar={() => setPagando(false)}
          // O pai relê a Secretaria inteira: o status do pedido mudou, e com ele
          // o selo, o botão e a lista de documentos. Recarregar só este card
          // deixaria os outros mostrando o estado de antes.
          aoMudar={() =>
            aoTerminar({
              tom: "ok",
              texto: "Comprovante enviado. A secretaria vai validar o pagamento.",
              mudou: true,
            })
          }
        />
      )}

      {/* ── POR QUE não pode pedir ─────────────────────────────────────────
          Vem ANTES do botão, e é a peça central desta seção. As cinco travas do
          certificado (000138) barrariam 35.897 das 39.252 matrículas medidas em
          01/09 — ou seja, para a maioria o botão está desligado, e um botão
          cinza sem explicação é lido como defeito da tela.

          O texto é do BANCO (`sec_pode_solicitar.detalhe`), num lugar só: a tela
          que remonta a frase a partir dos fatos foi a cicatriz do teto do MEI,
          onde a explicação passou a contradizer o número em quatro situações
          medidas. */}
      {!podePedir && travaViva && (
        <div
          data-trava={travaViva.motivo}
          className="mt-3 rounded-lg border border-laranja/30 bg-laranja/10 p-3"
        >
          <p className="text-sm text-navy-ink">{travaViva.detalhe}</p>
          {/* O motivo que o aluno resolve NO HUB ganha o caminho. Sem o link ele
              lê "complete o CPF no seu perfil" e vai procurar onde — é a lição
              do "Limite com a prorrogação": o que a tela não diz, o olho
              preenche com a suposição errada. */}
          {travaViva.motivo === "dados_incompletos" && (
            <Link
              to="/perfil"
              className="mt-2 inline-flex text-sm font-semibold text-laranja-claro underline decoration-laranja/40 underline-offset-2 hover:decoration-laranja"
            >
              Completar no meu perfil
            </Link>
          )}
          {travaViva.motivo === "avaliacoes_pendentes" && (
            <Link
              to="/faculdade/notas"
              className="mt-2 inline-flex text-sm font-semibold text-laranja-claro underline decoration-laranja/40 underline-offset-2 hover:decoration-laranja"
            >
              Ver as minhas avaliações
            </Link>
          )}
          {travaViva.motivo === "documentos_reprovados" && (
            <p className="mt-2 text-xs text-navy-mute">
              O motivo de cada reprovação está em <strong>Meus documentos</strong>, acima nesta
              página.
            </p>
          )}
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-3">
        {doCurso && (doCurso.anexo_path || doCurso.anexo_externo_url) && (
          <BotaoDeAnexo doc={doCurso} rotulo="Baixar" />
        )}
        {!emAndamento && (
          <button
            type="button"
            onClick={pedir}
            // A trava vale para pedido NOVO — decisão de Victor em 31/08. Pedido
            // já em andamento nem mostra este botão (o `!emAndamento` acima), e
            // "Pedir de novo" É pedido novo, então passa pela mesma trava.
            disabled={pedindo || !podePedir}
            data-pedir
            // DOIS estados desligados, e a diferença foi medida em 01/09.
            //
            // "Pedindo…" é transitório de um segundo: `pill-cta` com opacidade
            // comunica "aguarde", e ninguém fica lendo o rótulo. Travado é
            // permanente — as cinco travas do certificado barram 35.897 das
            // 39.252 matrículas —, e ali a opacidade dá **1,57 : 1** sobre navy,
            // porque ela compõe o texto junto com o fundo e os dois convergem.
            // `pill-desligada` tem cor própria e dá 6,99 : 1. O porquê inteiro,
            // com a tabela, está no `index.css`.
            className={podePedir ? "pill-cta disabled:opacity-55" : "pill-desligada"}
          >
            {pedindo ? "Pedindo…" : doCurso ? "Pedir de novo" : "Pedir"} <SetaDireita />
          </button>
        )}
      </div>

      {/* A linha "Também pedido em: <curso>" SAIU em 02/09, por pedido de Victor.
          Não recoloque.

          Ela existia por simetria com o card de ENVIO, onde a informação vale: um
          RG enviado numa pós serve na outra, e saber que ele já está lá evita o
          reenvio. No PEDIDO não vale — cada declaração é de um curso, e um pedido
          feito na Pós de Finanças não adianta nada para quem está olhando a Pós
          RT. O que a linha produzia era ruído em toda a lista de quem tem mais de
          um curso, e nenhuma ação possível. */}
    </div>
  );
}

function LinhaDeDocumento({
  doc,
  nomeDoCurso,
}: {
  // O mínimo que a linha desenha, e estrutural de propósito: ela serve ao pedido
  // (`MeuDocumento`) e ao envio fora do catálogo (`MeuEnvio`, desde a 000286), e
  // nenhum dos dois precisa fingir ser o outro. Sem `fluxo`, é envio.
  doc: Pick<
    MeuDocumento,
    "id" | "nome" | "status" | "curso_slug" | "criado_em" | "anexo_path" | "anexo_externo_url"
  > & { fluxo?: string };
  nomeDoCurso: (s: string | null) => string;
}) {
  const estado = estadoDoDocumento({ fluxo: doc.fluxo ?? "envio", status: doc.status });
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-navy-ink/10 pt-2 first:border-0 first:pt-0">
      <div className="min-w-48 flex-1">
        <p className="text-sm font-semibold text-navy-ink">{doc.nome}</p>
        <p className="text-xs text-navy-mute">
          {nomeDoCurso(doc.curso_slug)} · {data(doc.criado_em)}
        </p>
      </div>
      <div className="flex items-center gap-3">
        {(doc.anexo_path || doc.anexo_externo_url) && <BotaoDeAnexo doc={doc} rotulo="Ver" />}
        <Selo tom={estado.tom}>{estado.rotulo}</Selo>
      </div>
    </div>
  );
}

/**
 * Uma linha que é só NOME + procedência + o botão de baixar.
 *
 * Existe separada de `LinhaDeDocumento` porque as duas listas novas — o
 * entregue anterior (000260) e o contrato (000261) — não têm ESTADO: elas são
 * documentos prontos. `LinhaDeDocumento` desenha um `Selo` de estado, e forçar
 * um estado aqui seria inventar informação ("entregue" dito duas vezes, ou pior,
 * o selo de um fluxo ao qual o contrato não pertence mais).
 */
function LinhaDeArquivo({
  nome,
  detalhe,
  arquivo,
}: {
  nome: string;
  detalhe: string;
  arquivo: ComArquivo;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-navy-ink/10 pt-2 first:border-0 first:pt-0">
      <div className="min-w-48 flex-1">
        <p className="text-sm font-semibold text-navy-ink">{nome}</p>
        <p className="text-xs text-navy-mute">{detalhe}</p>
      </div>
      {/* Sem arquivo não há botão, e não há linha sem arquivo: as duas fontes
          só devolvem quem tem caminho ou url (a função filtra, e a constraint
          `contratos_arquivos_tem_arquivo` garante do outro lado). O `&&` fica
          porque o tipo admite nulo — prometer um botão que não abre nada é a
          cicatriz do `anexo_path` gravado antes do upload confirmar. */}
      {(arquivo.anexo_path || arquivo.anexo_externo_url) && (
        <BotaoDeAnexo doc={arquivo} rotulo="Baixar" />
      )}
    </div>
  );
}

/**
 * O botão que abre o anexo.
 *
 * Dois caminhos, e a ORDEM entre eles é a decisão: `anexo_path` está no bucket
 * do hub — nosso, privado, aberto por url assinada — e `anexo_externo_url` é um
 * endereço de fora que já funciona (os contratos da ZapSign, e o que ainda mora
 * no CDN do Bubble).
 *
 * **O bucket vem PRIMEIRO desde 03/09/2026, e antes vinha por último.** Enquanto
 * nada havia migrado, preferir a url externa era o certo: ela era a única que
 * existia. Depois da migração dos anexos (144.663 arquivos), a ordem antiga
 * virou um defeito silencioso — os arquivos estavam no hub e o aluno continuava
 * baixando do Bubble, porque `anexo_externo_url` é PRESERVADA de propósito (é o
 * caminho de volta se um upload tiver gravado errado). No dia do desligamento,
 * todos os botões quebrariam de uma vez, com os arquivos aqui do lado.
 *
 * O `anexo_externo_url` continua sendo o segundo ramo, e continua servindo: os
 * 9 pedidos que não migraram, e todo documento cujo arquivo vive fora.
 *
 * A url assinada é pedida no CLIQUE, não ao desenhar a lista: assinar 20 urls de
 * uma vez gastaria 20 chamadas para o aluno abrir uma, e cada uma expira em 5
 * minutos — as que ele não clicasse já teriam vencido.
 */
function BotaoDeAnexo({ doc, rotulo = "Ver arquivo" }: { doc: ComArquivo; rotulo?: string }) {
  const [abrindo, setAbrindo] = useState(false);

  if (doc.anexo_path) {
    return (
      <button
        type="button"
        disabled={abrindo}
        onClick={async () => {
          setAbrindo(true);
          const url = await urlDoAnexo(doc.anexo_path!);
          setAbrindo(false);
          if (url) window.open(url, "_blank", "noopener,noreferrer");
        }}
        className="text-sm font-semibold text-laranja-claro underline-offset-4 hover:underline disabled:opacity-60"
      >
        {abrindo ? "Abrindo…" : rotulo}
      </button>
    );
  }
  if (!doc.anexo_externo_url) return null;

  return (
    <a
      href={doc.anexo_externo_url}
      target="_blank"
      rel="noreferrer"
      className="text-sm font-semibold text-laranja-claro underline-offset-4 hover:underline"
    >
      {rotulo}
      <span className="sr-only"> (abre em outra aba)</span>
    </a>
  );
}
